import { useEffect, useState } from 'react'
import {
  eachDayOfInterval,
  endOfMonth,
  isSameDay,
  startOfMonth,
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { timeAbrevAMPM, timeAbrevUTC } from '~/utils/strings.ts'
import useMontlyParsedDates from '~/hooks/useMontlyParsedDates.tsx'

type UseServiceBookingProps = {
  service: any
  sessionToReschedule?: any
}

export function useServiceBooking({
  service,
  sessionToReschedule,
}: UseServiceBookingProps) {
  const [disabledDates, setDisabledDates] = useState<Date[]>([])
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined,
  )
  const [availableHours, setAvailableHours] = useState<
    {
      time24h: string
      time12h: string
    }[]
  >([])

  const {
    availableDates,
    monthAndYear,
    setFormatMonthAndYear,
    timeZone,
    setTimeZone,
  } = useMontlyParsedDates({
    service,
    sessionToReschedule,
  })

  useEffect(() => {
    const startOfMonthDate = startOfMonth(
      new Date(monthAndYear.year, monthAndYear.month, 1),
    )
    const endOfMonthDate = endOfMonth(
      new Date(monthAndYear.year, monthAndYear.month, 1),
    )
    const allDaysInMonth = eachDayOfInterval({
      start: startOfMonthDate,
      end: endOfMonthDate,
    })

    setDisabledDates(
      allDaysInMonth.flatMap(date => {
        const hasAvailableSlots = availableDates.find(
          availableDate =>
            isSameDay(
              utcToZonedTime(new Date(availableDate.date), 'UTC'),
              date,
            ) && availableDate.timeSlots.length > 0,
        )
        return hasAvailableSlots ? [] : date
      }),
    )
  }, [availableDates, monthAndYear])

  useEffect(() => {
    if (!selectedDate) return

    const selectedDateStartOfDay = new Date(selectedDate)
    selectedDateStartOfDay.setHours(0, 0, 0, 0)

    const dayData = availableDates.find(availableHour => {
      const availableDateStartOfDay = utcToZonedTime(
        new Date(availableHour.date),
        'UTC',
      )
      availableDateStartOfDay.setHours(0, 0, 0, 0)
      return (
        availableDateStartOfDay.getTime() === selectedDateStartOfDay.getTime()
      )
    })

    if (dayData) {
      setAvailableHours(
        dayData.timeSlots.map((date: Date) => ({
          time24h: timeAbrevUTC(new Date(date)),
          time12h: timeAbrevAMPM(new Date(date)),
        })),
      )
    } else {
      setAvailableHours([])
    }

    setSelectedTime(undefined)
  }, [selectedDate, availableDates])

  return {
    disabledDates,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    availableHours,
    setFormatMonthAndYear,
    timeZone,
    setTimeZone,
  }
}
